import { Drawer } from "antd";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { ReactComponent as Close } from "../../../assets/images/close-outline.svg";
import { getDDxHistory } from "../service";
import moment from "moment";
import { motion } from "framer-motion";

type HistoryDrawerProps = {
  open: boolean;
  onClose: () => void;
  onPressHistoryItem: (historyId: string, pastCaseDate?: string) => void;
};

const ShimmerLine = () => (
  <div className="relative w-full h-4 overflow-hidden bg-gray-200 rounded-md">
    <motion.div
      className="absolute top-0 left-0 w-2/3 h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"
      animate={{ x: ["-100%", "100%"] }}
      transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
    />
  </div>
);

const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  open,
  onClose,
  onPressHistoryItem,
}) => {
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const observer = useRef<IntersectionObserver | null>(null);

  const fetchHistoryList = async (currentPage: number) => {
    setLoading(true);
    const res = await getDDxHistory({ page: currentPage, pageSize: 10 });
    if (res?.status === 200) {
      setHistoryList((prev) => [...prev, ...(res?.data?.history ?? [])]);
      setHasMore(res?.data?.history?.length > 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setHistoryList([]);
      setPage(1);
      setHasMore(true);
      fetchHistoryList(1);
    }
  }, [open]);

  const lastItemRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    if (page > 1) fetchHistoryList(page);
  }, [page]);

  const formatDate = (dateString: string): string => {
    const momentDate = moment(dateString);
    const now = moment().startOf("day");
    const yesterday = moment().subtract(1, "day").startOf("day");

    if (momentDate.isSame(now, "day"))
      return `Today, ${momentDate.format("hh:mm A")}`;
    if (momentDate.isSame(yesterday, "day"))
      return `Yesterday, ${momentDate.format("hh:mm A")}`;

    return momentDate.format("DD/MM/YYYY, hh:mm A");
  };

  return (
    <Drawer
      title={"DDx History"}
      placement="right"
      closeIcon={false}
      open={open}
      extra={
        <button onClick={onClose}>
          <Close className="min-h-6 min-w-6" />
        </button>
      }
      onClose={onClose}
      className="rounded-l-3xl"
    >
      <div className="flex flex-col gap-2 overflow-auto no-scrollbar">
        {historyList.map((item, index) => {
          return (
            <button
              key={item._id}
              className="flex flex-col gap-2 border-b border-b-[#E2E2EA] pb-5"
              onClick={() => onPressHistoryItem(item._id, item.createdAt)}
              ref={index === historyList.length - 1 ? lastItemRef : null}
            >
              <p className="text-[#454551] font-semibold text-left text-sm">
                {formatDate(item.createdAt)}
              </p>
              <p className="text-[#454551] font-normal text-left text-xs line-clamp-2 overflow-hidden">
                {item.docstaReqTranscription}
              </p>
            </button>
          );
        })}
        {loading && (
          <div className="flex flex-col gap-1">
            {[1, 2, 3].map((_, idx) => (
              <div key={idx}>
                <ShimmerLine />
                <ShimmerLine />
                <ShimmerLine />
                <ShimmerLine />
                <div className="h-2" />
              </div>
            ))}
          </div>
        )}
        {!loading && historyList.length === 0 && (
          <div>
            <p className="text-base text-[#454551CC] text-center font-normal">
              No History Found!
            </p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default HistoryDrawer;
